<template>
  <v-form @submit.native.prevent="submit">
    <template v-if="isReady && hasInitAccountData">
      <v-row no-gutters justify="center" align="center" class="pb-5">
        <h3>Bienvenue sur Indeego</h3>
      </v-row>
      <div v-if="isDisclaimer">
        <v-row no-gutters justify="center" align="center">
          <div class="init-account-disclaimer">
            Afin de pouvoir accéder à Indeego vous devez terminer la configuration de votre compte.
            Cela ne vous prendra que quelques instants et votre accès sera ensuite finalisé.
          </div>
        </v-row>
        <v-row no-gutters justify="center" align="center">
          <v-btn
            dark
            large
            depressed
            class="n-secondary-btn mt-6 mb-8"
            @click="isDisclaimer = false"
          >
            C'est parti
            <v-icon right>chevron_right</v-icon>
          </v-btn>
        </v-row>
      </div>
      <template v-else>
        <v-alert type="error" class="mb-5" v-if="isServerError">
          Impossible d'initialiser votre compte, veuillez réessayer ou nous contacter grâce au chat en bas à droite
        </v-alert>
        <v-row justify="center" align="start">
          <v-col cols="6">
            <h4 class="d-block text-center pb-6">Qui êtes-vous ?</h4>
            <v-text-field
              v-model="firstname"
              :disabled="isSubmitting"
              dark
              outlined
              flat
              autofocus
              hide-details
              placeholder="Prénom"
            />
            <v-text-field
              v-model="lastname"
              :disabled="isSubmitting"
              dark
              outlined
              flat
              hide-details
              placeholder="Nom"
              class="mt-5"
            />
            <v-text-field
              v-model="email"
              :disabled="isSubmitting"
              dark
              outlined
              flat
              hide-details
              placeholder="Adresse email"
              class="mt-5"
            />
          </v-col>
          <v-col cols="6">
            <h4 class="d-block text-center pb-6">Quel mot de passe souhaitez-vous ?</h4>
            <v-alert type="info" text color="white" class="mb-5">6 caractères minimum</v-alert>
            <v-row no-gutters justify="center" align="center">
              <v-text-field
                v-model="password"
                type="password"
                :error="isPasswordError"
                :disabled="isSubmitting"
                dark
                outlined
                flat
                hide-details
                placeholder="Mot de passe"
                prepend-inner-icon="lock"
              />
            </v-row>
            <v-row no-gutters justify="center" align="center" class="pt-5">
              <v-text-field
                v-model="repeatPassword"
                type="password"
                :error="isRepeatPasswordError"
                :disabled="isSubmitting"
                dark
                outlined
                flat
                hide-details
                placeholder="Répétez le mot de passe"
                prepend-inner-icon="lock"
              />
            </v-row>
          </v-col>
        </v-row>
        <v-row no-gutters justify="center" align="center" class="mt-5 mb-2">
          <v-btn
            type="submit"
            dark
            large
            depressed
            :loading="isSubmitting"
            :disabled="!canSubmit"
            class="n-secondary-btn px-6"
          >
            Finaliser mon compte
            <v-icon right>check</v-icon>
          </v-btn>
        </v-row>
      </template>
    </template>
    <template v-else-if="!isReady">
      <v-row no-gutters justify="center" align="center" class="py-8">
        <v-progress-linear indeterminate color="white" style="width: 240px; max-width: 75%"/>
      </v-row>
    </template>
    <template v-else>
      <v-alert type="error" text color="white" class="mb-5">Ce lien n'est pas valide</v-alert>
    </template>
  </v-form>
</template>

<script>
import _ from 'lodash';
import { mapState } from 'vuex';

export default {
  data: () => ({
    isReady: false,
    isDisclaimer: true,
    isPasswordError: false,
    isRepeatPasswordError: false,
    isServerError: false,
    isSubmitting: false,
  }),

  computed: {
    initAccountToken: {
      get() { return this.$store.state[`ui/initAccount`].initAccountToken; },
      set(value) { this.$store.commit(`ui/initAccount/SET_INIT_ACCOUNT_TOKEN`, value); },
    },
    initAccountData: {
      get() { return this.$store.state[`ui/initAccount`].initAccountData; },
      set(value) { this.$store.commit(`ui/initAccount/SET_INIT_ACCOUNT_DATA`, value); },
    },
    email: {
      get() { return this.$store.state[`ui/initAccount`].email; },
      set(value) { this.$store.commit(`ui/initAccount/SET_EMAIL`, _.trim(value)); },
    },
    firstname: {
      get() { return this.$store.state[`ui/initAccount`].firstname; },
      set(value) { this.$store.commit(`ui/initAccount/SET_FIRSTNAME`, value); },
    },
    lastname: {
      get() { return this.$store.state[`ui/initAccount`].lastname; },
      set(value) { this.$store.commit(`ui/initAccount/SET_LASTNAME`, value); },
    },
    password: {
      get() { return this.$store.state[`ui/initAccount`].password; },
      set(value) { this.$store.commit(`ui/initAccount/SET_PASSWORD`, value); },
    },
    repeatPassword: {
      get() { return this.$store.state[`ui/initAccount`].repeatPassword; },
      set(value) { this.$store.commit(`ui/initAccount/SET_REPEAT_PASSWORD`, value); },
    },
    ...mapState(`model/auth`, [`accessToken`]),
    hasInitAccountData() {
      return Boolean(this.initAccountData?.email);
    },
    isPasswordLengthValid() {
      return this.password.length >= 6;
    },
    doPasswordsMatch() {
      return this.password === this.repeatPassword;
    },
    arePasswordsValid() {
      return this.isPasswordLengthValid && this.doPasswordsMatch;
    },
    canSubmit() {
      return this.$isEmail(this.email)
             && _.trim(this.firstname) !== ``
             && _.trim(this.lastname) !== ``
             && this.arePasswordsValid;
    },
  },

  methods: {
    async submit() {
      this.isPasswordError = false;
      this.isRepeatPasswordError = false;
      this.isServerError = false;

      if (!this.arePasswordsValid) {
        this.isPasswordError = true;
        this.isRepeatPasswordError = true;
      }

      if (this.isPasswordError || this.isRepeatPasswordError) { return; }

      this.isSubmitting = true;
      await this.$store.dispatch(`ui/initAccount/initAccount`);
      await this.$store.dispatch(`ui/login/login`);
      this.isSubmitting = false;

      if (!this.accessToken) {
        this.isServerError = true;
      } else {
        this.$router.replace({ name: `home` });
      }
    },
  },

  async beforeMount() {
    this.isReady = false;
    this.initAccountToken = this.$route.query.token || null;
    await this.$store.dispatch(`ui/initAccount/getInitAccountData`);
    this.email = this.initAccountData?.email || ``;
    this.firstname = this.initAccountData?.firstname || ``;
    this.lastname = this.initAccountData?.lastname || ``;
    this.isReady = true;
  },
};
</script>

<style lang="scss">
.init-account-disclaimer {
  width: 90%;
  max-width: 450px;
  text-align: justify;
  padding: 15px;
}
</style>
